import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { useGuinea, findPrefecture, isConakry, conakryCommuneOf, norm } from "@/lib/guinea";

const lbl = "text-xs font-bold uppercase tracking-wider";

export function PrefectureSelect({ geo, value, onChange, testid, placeholder = "Choisir une préfecture", allowAll }) {
  return (
    <Select value={value || (allowAll ? "all" : "")} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="mt-2">
        <SelectValue placeholder={geo ? placeholder : "Chargement…"} />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {allowAll && <SelectItem value="all">Toutes les préfectures</SelectItem>}
        {(geo?.regions || []).map((r) => (
          <SelectGroup key={r.id}>
            <SelectLabel className="text-[10px] font-bold uppercase tracking-widest text-primary">{r.name}</SelectLabel>
            {r.prefectures.map((p) => (
              <SelectItem key={p.name} value={p.name} data-testid={`${testid}-opt-${norm(p.name)}`}>
                {p.name}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

function SimpleSelect({ value, onChange, options, testid, placeholder, disabled }) {
  const opts = value && !options.some((o) => norm(o) === norm(value)) ? [value, ...options] : options;
  const current = opts.find((o) => norm(o) === norm(value)) || "";
  return (
    <Select value={current} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className="mt-2">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {opts.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-opt-${norm(o)}`}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function LocationFields({ city, quartier, address, onChange }) {
  const geo = useGuinea();
  const pref = findPrefecture(geo, city);
  const conakry = isConakry(pref);
  const [commune, setCommune] = useState("");

  useEffect(() => {
    if (conakry && quartier) setCommune((c) => c || conakryCommuneOf(pref, quartier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conakry, pref?.name, quartier]);

  useEffect(() => {
    if (pref && pref.name !== city) onChange({ city: pref.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref?.name]);

  const communeObj = pref?.communes?.find((c) => c.name === commune);

  return (
    <div className="space-y-5" data-testid="location-fields">
      <div>
        <Label className={lbl}>Préfecture / ville</Label>
        <PrefectureSelect
          geo={geo}
          value={pref?.name || city}
          testid="listing-city"
          onChange={(v) => {
            setCommune("");
            onChange({ city: v, quartier: "" });
          }}
        />
        {pref && <div className="mt-1 text-xs text-muted-foreground">Région administrative : {pref.admin_region}</div>}
      </div>

      {conakry ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className={lbl}>Commune</Label>
            <SimpleSelect
              value={commune}
              options={pref.communes.map((c) => c.name)}
              testid="listing-commune"
              placeholder="Choisir une commune"
              onChange={(v) => {
                setCommune(v);
                onChange({ quartier: "" });
              }}
            />
          </div>
          <div>
            <Label className={lbl}>Quartier</Label>
            <SimpleSelect
              value={quartier}
              options={communeObj?.quartiers || []}
              testid="listing-quartier"
              placeholder={commune ? "Choisir un quartier" : "Choisissez d'abord la commune"}
              disabled={!commune}
              onChange={(v) => onChange({ quartier: v })}
            />
          </div>
        </div>
      ) : (
        <div>
          <Label className={lbl}>Commune / sous-préfecture</Label>
          <SimpleSelect
            value={quartier}
            options={(pref?.communes || []).map((c) => c.name)}
            testid="listing-quartier"
            placeholder={pref ? "Choisir une commune ou sous-préfecture" : "Choisissez d'abord la préfecture"}
            disabled={!pref}
            onChange={(v) => onChange({ quartier: v })}
          />
        </div>
      )}

      <div>
        <Label htmlFor="address" className={lbl}>
          Précision (rue, repère) <span className="font-normal normal-case text-muted-foreground">— facultatif</span>
        </Label>
        <Input
          id="address"
          data-testid="listing-address"
          value={address || ""}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="Ex : près de la mosquée, après le carrefour…"
          className="mt-2"
        />
      </div>
    </div>
  );
}
